import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { PrimeModelCreationData, PrimeProcess, PrimeArtifactState } from '../../types/primeModel';
import { PrimeArtifact } from '../../types/primeArtifact';
import { createPrimeModel } from '../../store/actions/primeArtifactActions';
import { PrimeArtifactForm } from './PrimeArtifactForm';
import { PrimeProcessForm } from './PrimeProcessForm';
import PrimeStateForm from './PrimeStateForm';
import PrimeTransitionForm from './PrimeTransitionForm';
import { Omit } from 'utility-types';
import '../../styles/primeModel.css';

type Step = 'artifact' | 'states' | 'transitions' | 'processes' | 'review';

type PrimeArtifactFormData = Omit<PrimeArtifact, 'created_at' | 'updated_at' | 'children'>;

interface PrimeModelCreationProps {
  parentId: string | null;
  onComplete: () => void;
  onCancel: () => void;
}

export const PrimeModelCreation: React.FC<PrimeModelCreationProps> = ({ onComplete, onCancel }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [currentStep, setCurrentStep] = useState<Step>('artifact');
  const [modelData, setModelData] = useState<PrimeModelCreationData>({
    artifact: {
      id: '',
      name: '',
      description: '',
      artifact_type_id: '',
      artifact_type_name: '',
      parent_ref: undefined,
      parent_name: undefined,
      prime_artifact_id: undefined,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      children: [],
    },
    states: [],
    transitions: [],
    processes: [],
  });

  const handleArtifactSubmit = (artifact: PrimeArtifactFormData) => {
    const fullArtifact: PrimeArtifact = {
      ...artifact,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      children: [],
    };
    setModelData((prev) => ({ ...prev, artifact: fullArtifact }));
    setCurrentStep('states');
  };

  const handleStatesSubmit = (state: PrimeArtifactState) => {
    setModelData((prev) => ({ ...prev, states: [...prev.states, state] }));
    setCurrentStep('transitions');
  };

  const handleTransitionsSubmit = (transitions: PrimeModelCreationData['transitions']) => {
    setModelData((prev) => ({ ...prev, transitions }));
    setCurrentStep('processes');
  };

  const handleProcessesSubmit = (process: PrimeProcess) => {
    setModelData((prev) => ({ ...prev, processes: [process] }));
    setCurrentStep('review');
  };

  const handleComplete = async () => {
    try {
      await dispatch(createPrimeModel(modelData)).unwrap();
      onComplete();
    } catch (error) {
      console.error('Failed to create prime model:', error);
    }
  };

  const handleBack = () => {
    switch (currentStep) {
      case 'states':
        setCurrentStep('artifact');
        break;
      case 'transitions':
        setCurrentStep('states');
        break;
      case 'processes':
        setCurrentStep('transitions');
        break;
      case 'review':
        setCurrentStep('processes');
        break;
      default:
        break;
    }
  };

  const getStateName = (stateId: string) => {
    const state = modelData.states.find((s) => s.id === stateId);
    return state ? state.name : 'Unknown State';
  };

  return (
    <div className="prime-model-creation">
      <div className="step-indicator">
        <div className={`step ${currentStep === 'artifact' ? 'active' : ''}`}>Artifact</div>
        <div className={`step ${currentStep === 'states' ? 'active' : ''}`}>States</div>
        <div className={`step ${currentStep === 'transitions' ? 'active' : ''}`}>Transitions</div>
        <div className={`step ${currentStep === 'processes' ? 'active' : ''}`}>Processes</div>
        <div className={`step ${currentStep === 'review' ? 'active' : ''}`}>Review</div>
      </div>

      <div className="step-content">
        {currentStep === 'artifact' && (
          <PrimeArtifactForm initialData={modelData.artifact} onSubmit={handleArtifactSubmit} />
        )}

        {currentStep === 'states' && (
          <PrimeStateForm initialData={{}} onSubmit={handleStatesSubmit} onBack={handleBack} />
        )}

        {currentStep === 'transitions' && (
          <PrimeTransitionForm
            prime_artifact_id={modelData.artifact.id}
            primeStates={modelData.states}
            primeProcesses={modelData.processes}
            initialData={modelData.transitions}
            onSubmit={(data: unknown) =>
              handleTransitionsSubmit(data as PrimeModelCreationData['transitions'])
            }
            onBack={handleBack}
          />
        )}

        {currentStep === 'processes' && (
          <PrimeProcessForm
            initialData={modelData.processes[0]}
            onSubmit={handleProcessesSubmit}
            onCancel={handleBack}
          />
        )}

        {currentStep === 'review' && (
          <div className="review-step">
            <h2>Review Prime Model</h2>
            <div className="review-content">
              <div className="review-section">
                <h3>Artifact</h3>
                <p>
                  <strong>Name:</strong> {modelData.artifact.name}
                </p>
                <p>
                  <strong>Description:</strong> {modelData.artifact.description}
                </p>
                <p>
                  <strong>Type ID:</strong> {modelData.artifact.artifact_type_id}
                </p>
              </div>

              <div className="review-section">
                <h3>States</h3>
                <ul>
                  {modelData.states.map((state) => (
                    <li key={state.id}>{state.name}</li>
                  ))}
                </ul>
              </div>

              <div className="review-section">
                <h3>Transitions</h3>
                <ul>
                  {modelData.transitions.map((transition) => (
                    <li key={transition.id}>
                      {getStateName(transition.from_state_ref)} →{' '}
                      {getStateName(transition.to_state_ref)} ({transition.default_transition_type})
                    </li>
                  ))}
                </ul>
              </div>

              <div className="review-section">
                <h3>Processes</h3>
                <ul>
                  {modelData.processes.map((process) => (
                    <li key={process.id}>{process.name}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="form-actions">
              <button type="button" onClick={handleBack}>
                Back
              </button>
              <button type="button" onClick={handleComplete}>
                Create Model
              </button>
              <button type="button" onClick={onCancel}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
